/**
 * DaVinci API Booking Details Extractor
 *
 * Extracts supplementary details from DaVinci BA (Booking Action) response,
 * such as meal descriptions and important hints.
 */

/** Requirement code marking a package travel (Pauschalreise) service line. */
export const PACKAGE_REQUIREMENT_CODE = 'PRK';

/**
 * True iff the BA booking response contains a service line with
 * Requirement === 'PRK' (package travel → "Formblatt zur Pauschalreiserichtlinie").
 * Checking the Requirement is sufficient; Service ("PACKAGE") is not required.
 *
 * @param bookingResponse - The Booking object from DaVinci BA response
 */
export function bookingResponseHasPackageRequirement(
  bookingResponse: Record<string, unknown>
): boolean {
  const services = bookingResponse.Services as Array<Record<string, unknown>> | undefined;
  return (
    Array.isArray(services) && services.some((s) => s.Requirement === PACKAGE_REQUIREMENT_CODE)
  );
}

/**
 * Extracted details from DaVinci BA response
 */
export interface ExtractedBookingDetails {
  /**
   * Full description of the meal plan (e.g., "Halbpension")
   * Extracted from the first service's MealPlanCodeDetails.Description
   */
  mealDescription: string | null;

  /**
   * Strings from Booking.Hints[] where PopupMode === 1 (shown to the user as popup/note).
   */
  popupHints: string[] | null;

  /**
   * Agency/organizer name (e.g., "Vamos Eltern-Kind-Reisen GmbH")
   * Extracted from Booking.Agency.Name
   */
  agencyName: string | null;

  /**
   * Tour operator code (e.g., "VAMOS", "PARTNER")
   * Extracted from Booking.Touroperator
   */
  tourOperator: string | null;
}

/**
 * Extract details from DaVinci BA (Booking Action) response
 *
 * @param bookingResponse - The Booking object from DaVinci BA response
 * @returns Extracted details
 */
export function extractDetailsFromBookingResponse(
  bookingResponse: Record<string, unknown>
): ExtractedBookingDetails {
  const result: ExtractedBookingDetails = {
    mealDescription: null,
    popupHints: null,
    agencyName: null,
    tourOperator: null,
  };

  // Extract meal description from the first service (assuming it's consistent)
  const services = bookingResponse.Services as Array<Record<string, unknown>> | undefined;
  if (Array.isArray(services) && services.length > 0) {
    const firstService = services[0];
    const mealDetails = firstService.MealPlanCodeDetails as Record<string, unknown> | undefined;
    if (mealDetails && typeof mealDetails.Description === 'string') {
      result.mealDescription = mealDetails.Description;
    }
  }

  // Extract popup hints (shown to the user in the order summary)
  const hints = bookingResponse.Hints as Array<Record<string, unknown>> | undefined;
  if (Array.isArray(hints)) {
    const popupHints = hints
      .filter(
        (hint) =>
          typeof hint.PopupMode === 'number' &&
          hint.PopupMode === 1 &&
          typeof hint.Info === 'string'
      )
      .map((hint) => (hint.Info as string).replace(/^\d+\)\s*/, ''));

    if (popupHints.length > 0) {
      result.popupHints = popupHints;
    }
  }

  // Extract agency/organizer name
  const agency = bookingResponse.Agency as Record<string, unknown> | undefined;
  if (agency && typeof agency.Name === 'string' && agency.Name.trim() !== '') {
    result.agencyName = agency.Name;
  }

  // Extract tour operator code
  const touroperator = bookingResponse.Touroperator;
  if (typeof touroperator === 'string' && touroperator.trim() !== '') {
    result.tourOperator = touroperator;
  }

  return result;
}
